import React from "react";
import { connect } from "react-redux";
import language from "../../../language/index.js";
import toastMessage from "../../../utils/toastMessage.js";
import api from "../../../utils/api/index.js";
import Input from "../../Input/Input.js";
import Button from "../../Button/Button.js";
import { getStorage } from "../../../utils/storage.js";
import Select from "../../Input/Select.js";
import { fetchProductTypes } from "../../../utils/queries/productsType/index.js";
import { fetchDiagnosisOptions } from "../../../utils/queries/diagnosisOptions.js";
import { searchICD11 } from "../../../utils/queries/icd11.js";

// eslint-disable-next-line no-unused-vars
let copyData = [];

const toTitleCase = (str = "") =>
  str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

// Strip "CODE001 - " prefix and normalise casing
const friendlyName = (label = "") => {
  const stripped = label.replace(/^[A-Z0-9]+\s*-\s*/i, "");
  return toTitleCase(stripped);
};

// Volume cap rules: { nameFragment (lowercase) -> { maxVol, suggestCode } }
const VOL_RULES = {
  pediatric: {
    maxVol: 250,
    warning:
      "Volume for Red Blood Cells - Pediatrics must not exceed 250 ml. " +
      "If a larger volume is needed, please request Red Blood Cells - Adult instead.",
  },
};

const getVolWarning = (ptLabel = "", volume) => {
  if (!volume || volume === "") return null;
  const lower = ptLabel.toLowerCase();
  for (const [fragment, rule] of Object.entries(VOL_RULES)) {
    if (lower.includes(fragment) && parseFloat(volume) > rule.maxVol) {
      return rule.warning;
    }
  }
  return null;
};

class NewRequest extends React.Component {
  state = {
    error: {},
    user: {},
    allProducts: [],
    requestStock: "",
    volume: "",
    updatedListOfProducts: [],
    productType: null,
    diagnosisOptions: [],
    icd11: null,
    diagnosisMode: "predefined",
    // { [productTypeValue]: { requestStock: "", volume: "" } }
    productValues: {},
  };

  icd11Debounce = null;

  loadICD11Options = (inputValue) =>
    new Promise((resolve) => {
      if (this.icd11Debounce) clearTimeout(this.icd11Debounce);
      this.icd11Debounce = setTimeout(async () => {
        resolve(await searchICD11(inputValue));
      }, 350);
    });

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user });

    await this.getDiagnosisOptions();

    if (this.props.isForNominative) {
      await this.getNominativeProducts(true);
    }

    if (this.props?.selectedData?._id) {
      const diagnosisValue = this.props?.selectedData?.diagnosis;
      const diagnosisOptions = this.state.diagnosisOptions;
      const diagnosis = diagnosisValue
        ? diagnosisOptions.find((el) => el.value === diagnosisValue) || {
            label: diagnosisValue,
            value: diagnosisValue,
          }
        : null;

      const sd = this.props.selectedData;
      const icd11 = sd?.diagnosisCode
        ? {
            value: sd.diagnosisCode,
            label: `${sd.diagnosisCode} — ${sd.diagnosisTitle || ""}`,
            code: sd.diagnosisCode,
            title: sd.diagnosisTitle,
            icdId: sd.diagnosisIcdId,
            release: sd.diagnosisRelease,
          }
        : null;

      this.setState({
        ...sd,
        diagnosis,
        icd11,
        diagnosisMode: icd11 ? "icd11" : "predefined",
        productType: sd.productType
          ? { label: sd.productType.name, value: sd.productType._id, ...sd.productType }
          : null,
        requestStock: sd.numberOfPackages ?? "",
        volume: sd.volume ?? "",
      });
    }
  };

  getDiagnosisOptions = async () => {
    const diagnosisOptions = await fetchDiagnosisOptions(
      this.props.defaultLanguage,
      { inputType: "dropdown", page: 1, limit: 500 }
    );
    this.setState({ diagnosisOptions: diagnosisOptions || [] });
  };

  getNominativeProducts = async (isFetchingProduct, search) => {
    try {
      this.setState({ isLoading: isFetchingProduct });
      const data = await fetchProductTypes(this.props.defaultLanguage, {
        inputType: "dropdown",
        q: search || undefined,
        page: 1,
        limit: 100,
      });
      this.setState({
        isLoading: false,
        allProducts: data.filter((el) => el.code !== "BIO001"),
        updatedListOfProducts: data.filter((el) => el.code !== "BIO001"),
      });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  };

  setDiagnosisMode = (mode) => {
    this.setState({
      diagnosisMode: mode,
      diagnosis: null,
      icd11: null,
      otherDiagnosis: "",
      error: { ...this.state.error, diagnosis: undefined },
    });
  };

  onChangeText = (field, e) => {
    const { error } = this.state;
    delete error[field];

    const crossClear =
      field === "diagnosis" && e ? { icd11: null }
      : field === "icd11" && e ? { diagnosis: null, otherDiagnosis: "" }
      : {};

    this.setState({
      [field]: e?.target ? e.target.value
        : field === "productType" ? { ...e, currentStock: e?.currentStock || 0 }
        : e,
      ...crossClear,
      error,
    });
  };

  updateProductValue = (key, field, value) => {
    this.setState((prev) => ({
      productValues: {
        ...prev.productValues,
        [key]: {
          ...(prev.productValues[key] || { requestStock: "", volume: "" }),
          [field]: value,
        },
      },
    }));
  };

  validateForm() {
    const { error, productType, diagnosis, icd11 } = this.state;

    if (this.props?.selectedData?._id) {
      if (!productType) {
        error.productType = language[this.props.defaultLanguage].product_type_required;
      } else {
        delete error.productType;
      }
    } else {
      const hasAny = Object.values(this.state.productValues).some(
        (v) =>
          (v.requestStock !== "" && v.requestStock != null) ||
          (v.volume !== "" && v.volume != null)
      );
      if (!hasAny) {
        error.productValues =
          language[this.props.defaultLanguage].product_type_required ||
          "Enter quantity for at least one product.";
      } else {
        delete error.productValues;
      }

      // Check volume rules
      const { updatedListOfProducts, productValues } = this.state;
      for (const pt of updatedListOfProducts) {
        const row = productValues[pt.value];
        if (!row) continue;
        const warning = getVolWarning(pt.label, row.volume);
        if (warning) {
          error[`vol_${pt.value}`] = warning;
        } else {
          delete error[`vol_${pt.value}`];
        }
      }
    }

    if (!diagnosis && !icd11) {
      error.diagnosis = language[this.props.defaultLanguage].diagnosis_required;
    } else {
      delete error.diagnosis;
    }

    this.setState({ error });
  }

  onSubmit = async () => {
    try {
      await this.validateForm();
      const { error, diagnosis, icd11, otherDiagnosis } = this.state;

      // Block if any volume violation exists
      const hasVolError = Object.keys(error).some((k) => k.startsWith("vol_"));
      if (Object.keys(error).length > 0 || hasVolError) return;

      this.setState({ isSubmitting: true });

      const diagnosisPayload = {
        diagnosis: diagnosis?.value || icd11?.title || "",
        otherDiagnosis: diagnosis?.value === "other" ? otherDiagnosis : "",
        diagnosisCode: icd11?.code || undefined,
        diagnosisTitle: icd11?.title || undefined,
        diagnosisIcdId: icd11?.icdId || undefined,
        diagnosisRelease: icd11?.release || undefined,
      };

      let hasError = false;

      if (this.props?.selectedData?._id) {
        const { productType, volume, requestStock } = this.state;
        const editPayload = {
          type: "nominative",
          request: this.props.request,
          productType: productType.value,
          ...diagnosisPayload,
          id: this.props.selectedData._id,
        };
        editPayload.numberOfPackages = (requestStock !== "" && requestStock != null) ? requestStock : null;
        editPayload.volume = (volume !== "" && volume != null) ? volume : null;
        try {
          await api({
            method: "PUT",
            url: `${process.env.REACT_APP_BBMS_BASE_API}/product-request`,
            data: editPayload,
          });
        } catch (err) {
          hasError = true;
          toastMessage("error", err);
        }
      } else {
        const { productValues, updatedListOfProducts } = this.state;
        for (const pt of updatedListOfProducts) {
          const row = productValues[pt.value];
          if (!row) continue;
          const hasQty = row.requestStock !== "" && row.requestStock != null;
          const hasVol = row.volume !== "" && row.volume != null;
          if (!hasQty && !hasVol) continue;
          try {
            await api({
              method: "POST",
              url: `${process.env.REACT_APP_BBMS_BASE_API}/product-request`,
              data: {
                type: "nominative",
                request: this.props.request,
                productType: pt.value,
                ...(hasQty ? { numberOfPackages: row.requestStock } : {}),
                ...(hasVol ? { volume: row.volume } : {}),
                ...diagnosisPayload,
              },
            });
          } catch (err) {
            hasError = true;
            toastMessage("error", err);
          }
        }
      }

      this.setState({ isSubmitting: false });
      this.props.getData && this.props.getData();

      if (!hasError) {
        this.setState({
          requestStock: "",
          volume: "",
          diagnosis: null,
          otherDiagnosis: "",
          icd11: null,
          productValues: {},
        });
        toastMessage("success", language[this.props.defaultLanguage].data_added_success);
        this.props.handleClose && this.props.handleClose();
      }
    } catch (error) {
      this.setState({ isSubmitting: false });
      toastMessage("error", error);
      this.props.getData && this.props.getData();
    }
  };

  renderDiagnosisSection() {
    const { defaultLanguage } = this.props;
    const { diagnosisMode, diagnosis, icd11, diagnosisOptions, error } = this.state;

    return (
      <>
        <div style={{ marginBottom: "0.25rem" }}>
          <label style={{ fontSize: "var(--font-size)", fontWeight: "var(--font-weight-md)" }}>
            {language[defaultLanguage].diagnosis}
            <span style={{ color: "var(--danger-color)", marginLeft: 2 }}>*</span>
          </label>
        </div>

        {/* Toggle */}
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            marginBottom: "0.75rem",
            padding: "4px",
            backgroundColor: "var(--text-color-10)",
            borderRadius: "32px",
          }}
        >
          {[
            { mode: "predefined", icon: "format_list_bulleted", label: language[defaultLanguage].predefined || "Predefined" },
            { mode: "icd11", icon: "travel_explore", label: "ICD-11" },
          ].map(({ mode, icon, label }) => {
            const isActive = diagnosisMode === mode;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => this.setDiagnosisMode(mode)}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.4rem",
                  padding: "0.45rem 1rem",
                  borderRadius: "28px",
                  border: isActive ? "2px solid var(--primary-color)" : "2px solid transparent",
                  backgroundColor: isActive ? "var(--light-color)" : "transparent",
                  color: isActive ? "var(--primary-color)" : "var(--text-color-60)",
                  fontWeight: 600,
                  fontSize: "var(--font-size-sm)",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: isActive ? "var(--card-shadow)" : "none",
                }}
              >
                <span className={process.env.REACT_APP_ICONS_TYPE} style={{ fontSize: "16px" }}>
                  {icon}
                </span>
                {label}
              </button>
            );
          })}
        </div>

        {diagnosisMode === "predefined" && (
          <>
            <Select
              options={diagnosisOptions}
              placeholder={language[defaultLanguage].select}
              value={diagnosis}
              onChange={(e) => this.onChangeText("diagnosis", e)}
              error={error.diagnosis}
            />
            {diagnosis?.value === "other" && (
              <Input
                label={language[defaultLanguage].other_please_specify}
                placeholder={language[defaultLanguage].other_placeholder}
                value={this.state.otherDiagnosis || ""}
                onChange={(e) => this.onChangeText("otherDiagnosis", e)}
                error={error.otherDiagnosis}
              />
            )}
          </>
        )}

        {diagnosisMode === "icd11" && (
          <Select
            loadOptions={this.loadICD11Options}
            defaultOptions={[]}
            placeholder={language[defaultLanguage].search}
            value={icd11}
            onChange={(e) => this.onChangeText("icd11", e)}
            error={error.diagnosis}
            helperText={
              icd11?.isFallback
                ? "ICD-11 service unavailable — showing locally configured diagnoses."
                : "Type at least 2 characters to search ICD-11 codes."
            }
          />
        )}
      </>
    );
  }

  render() {
    const isEditing = !!this.props?.selectedData?._id;
    const { defaultLanguage } = this.props;
    const { updatedListOfProducts, productValues, error } = this.state;

    // Count selected products for the guide counter
    const filledCount = Object.values(productValues).filter(
      (v) =>
        (v.requestStock !== "" && v.requestStock != null) ||
        (v.volume !== "" && v.volume != null)
    ).length;

    return (
      <>
        {/* Remove Input component's default margin inside our table so rows stay compact */}
        <style>{`
          .nr-product-table .input-container { margin: 0 !important; }
          .nr-product-table .input-container .helper-section { display: none; }
        `}</style>
        <div className="card-body">
          {/* ── Diagnosis ─────────────────────────────────────────────── */}
          <div className="mb-3">
            {this.renderDiagnosisSection()}
          </div>

          {/* ── Guide banner (add mode only) ───────────────────────────── */}
          {!isEditing && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "0.5rem",
                backgroundColor: "#EEF4FF",
                border: "1px solid #C7D7FA",
                borderRadius: "8px",
                padding: "0.6rem 0.85rem",
                marginBottom: "0.75rem",
                fontSize: "var(--font-size-sm)",
                color: "#2D4BA0",
              }}
            >
              <span
                className={process.env.REACT_APP_ICONS_TYPE}
                style={{ fontSize: "18px", flexShrink: 0, marginTop: "1px" }}
              >
                info
              </span>
              <div>
                <strong>How to fill this form:</strong> For each product you need,
                enter the <strong>quantity</strong>, the <strong>volume (ml)</strong>,
                or both. Leave a row completely empty to skip it.{" "}
                <span style={{ color: "#8B0000", fontWeight: 600 }}>
                  Red Blood Cells – Pediatrics volume must not exceed 250 ml.
                </span>
              </div>
            </div>
          )}

          {/* ── Products table ─────────────────────────────────────────── */}
          <div>
            {error.productValues && (
              <div
                style={{
                  color: "var(--danger-color)",
                  fontSize: "var(--font-size-sm)",
                  marginBottom: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.35rem",
                }}
              >
                <span className={process.env.REACT_APP_ICONS_TYPE} style={{ fontSize: "16px" }}>
                  error
                </span>
                {error.productValues}
              </div>
            )}

            <div style={{ overflowX: "auto" }}>
              <table
                className="table table-sm table-bordered nr-product-table"
                style={{ minWidth: "460px", marginBottom: 0 }}
              >
                <thead>
                  <tr style={{ backgroundColor: "var(--text-color-10, #f5f5f5)" }}>
                    <th style={{ width: "44%", fontWeight: 600, verticalAlign: "middle" }}>
                      {language[defaultLanguage].product_type || "Product"}
                    </th>
                    <th style={{ width: "28%", fontWeight: 600 }}>
                      Quantity
                      <div style={{ fontSize: "0.7rem", fontWeight: 400, color: "var(--text-color-60)", marginTop: "1px" }}>
                        units / doses
                      </div>
                    </th>
                    <th style={{ width: "28%", fontWeight: 600 }}>
                      Volume
                      <div style={{ fontSize: "0.7rem", fontWeight: 400, color: "var(--text-color-60)", marginTop: "1px" }}>
                        ml per unit
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {isEditing ? (
                    // ── Edit: one editable row ────────────────────────────
                    <tr>
                      <td style={{ verticalAlign: "middle" }}>
                        <div style={{ fontWeight: 600, fontSize: "var(--font-size-sm)" }}>
                          {friendlyName(this.state.productType?.label || "") || this.state.productType?.label || "—"}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            marginTop: "3px",
                            fontSize: "0.7rem",
                            color: "var(--text-color-60)",
                          }}
                        >
                          <span className={process.env.REACT_APP_ICONS_TYPE} style={{ fontSize: "13px" }}>
                            info
                          </span>
                          To change the product type, delete this product and add a new one.
                        </div>
                      </td>
                      <td>
                        <Input
                          placeholder={
                            this.state.productType?.label?.toUpperCase().includes("PLT001")
                              ? "No. of doses"
                              : "No. of packages"
                          }
                          value={this.state.requestStock || ""}
                          onChange={(e) => this.onChangeText("requestStock", e)}
                          type="number"
                          min={1}
                        />
                      </td>
                      <td>
                        <Input
                          placeholder="Enter volume"
                          value={this.state.volume || ""}
                          onChange={(e) => this.onChangeText("volume", e)}
                          type="text"
                          inputMode="decimal"
                          unit="ml"
                        />
                        {getVolWarning(
                          this.state.productType?.label || "",
                          this.state.volume
                        ) && (
                          <div style={{ color: "#B45309", fontSize: "0.72rem", marginTop: "0.25rem", lineHeight: 1.3 }}>
                            ⚠ Max 250 ml — switch to Red Blood Cells – Adult.
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    // ── Add: one row per product ──────────────────────────
                    updatedListOfProducts.map((pt, idx) => {
                      const val = productValues[pt.value] || {};
                      const name = friendlyName(pt.label);
                      const isFilled =
                        (val.requestStock !== "" && val.requestStock != null) ||
                        (val.volume !== "" && val.volume != null);
                      const volWarning = getVolWarning(pt.label, val.volume);
                      const isPlt = pt.label?.toUpperCase().includes("PLT001");

                      return (
                        <tr
                          key={pt.value}
                          style={{
                            backgroundColor: isFilled
                              ? "rgba(220, 240, 255, 0.35)"
                              : idx % 2 === 0
                              ? "transparent"
                              : "var(--text-color-5, #fafafa)",
                            transition: "background-color 0.15s",
                          }}
                        >
                          {/* Product name */}
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={{ fontWeight: isFilled ? 700 : 500, fontSize: "var(--font-size-sm)" }}>
                              {name}
                            </div>
                            {/* Inline hint */}
                            {pt.label?.toLowerCase().includes("pediatric") && (
                              <div style={{ fontSize: "0.7rem", color: "#8B0000", marginTop: "2px" }}>
                                Max volume: 250 ml per unit
                              </div>
                            )}
                            {isPlt && (
                              <div style={{ fontSize: "0.7rem", color: "var(--text-color-60)", marginTop: "2px" }}>
                                Unit: 3.0 × 10¹¹ platelets per dose
                              </div>
                            )}
                          </td>

                          {/* Packages / Doses */}
                          <td style={{ verticalAlign: "top" }}>
                            <Input
                              placeholder={isPlt ? "No. of doses" : "No. of packages"}
                              value={val.requestStock || ""}
                              onChange={(e) =>
                                this.updateProductValue(pt.value, "requestStock", e.target.value)
                              }
                              type="number"
                              min={1}
                            />
                          </td>

                          {/* Volume */}
                          <td style={{ verticalAlign: "top" }}>
                            <Input
                              placeholder="Enter volume"
                              value={val.volume || ""}
                              onChange={(e) =>
                                this.updateProductValue(pt.value, "volume", e.target.value)
                              }
                              type="text"
                              inputMode="decimal"
                              unit="ml"
                            />
                            {volWarning && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "0.25rem",
                                  backgroundColor: "#FEF3C7",
                                  border: "1px solid #F59E0B",
                                  borderRadius: "6px",
                                  padding: "0.35rem 0.5rem",
                                  marginTop: "0.35rem",
                                  fontSize: "0.7rem",
                                  color: "#92400E",
                                  lineHeight: 1.35,
                                }}
                              >
                                <span
                                  className={process.env.REACT_APP_ICONS_TYPE}
                                  style={{ fontSize: "14px", flexShrink: 0 }}
                                >
                                  warning
                                </span>
                                {volWarning}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Footer guide (add mode) ──────────────────────────── */}
            {!isEditing && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: "0.5rem",
                  fontSize: "var(--font-size-sm)",
                  color: "var(--text-color-60)",
                }}
              >
                <span>
                  {filledCount === 0
                    ? "No products selected yet"
                    : `${filledCount} product${filledCount > 1 ? "s" : ""} selected`}
                </span>
                {filledCount > 0 && (
                  <span style={{ color: "var(--primary-color)", fontWeight: 600 }}>
                    Ready to submit
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <Button
            text={language[defaultLanguage].close}
            onPress={() => this.props.handleClose && this.props.handleClose()}
            className="btn-default"
          />
          <Button
            text={language[defaultLanguage].submit}
            onPress={() => this.onSubmit()}
            isSubmitting={this.state.isSubmitting}
          />
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(NewRequest);
